// ============================================================================
// useAvatarActions
// Dispatcher hook for avatar recommended-action buttons. Holds which action
// is currently being applied (drives ActionModalHost), and exposes one
// `apply*` function per action type that writes the entity, then PATCHes the
// action's state on the contribution — the single audited place state changes.
// ============================================================================

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { createFeature, createADR } from '../../../services/workshop';
import { updateStoryContent } from '../../../services/n8n';
import { useRaidMutations } from '../../../hooks/useRaidItems';
import { useMilestoneMutations } from '../../../hooks/useMilestones';
import { RaidItemInput } from '../../../services/raid';
import { RaidLink } from '../../../types/raid';
import { AvatarRecommendedAction } from '../../../types/avatarActions';
import { sessionTasksService } from '../../../services/sessionTasksService';
import { getAvatarTriggerableTask } from './avatarTriggerableTasks';

export interface PendingAvatarAction {
  action: AvatarRecommendedAction;
  contributionId: string;
}

interface AvatarActionsScope {
  sessionId?: string;
  projectId?: string;
  /**
   * Called after an accepted action writes a requirement entity. The
   * requirements blob rendered by RequirementsPanel is plain useState in
   * LiveSession (see loadRequirements/reloadRequirementsData), not
   * react-query — invalidating the 'project-requirements' query key alone
   * only refreshes useMilestones/useRaidItems, not that blob. Pass
   * LiveSession's reloadRequirementsData here so accepted creates/mutations
   * actually show up without waiting for the next natural reload.
   */
  onRequirementsChanged?: () => void | Promise<void>;
}

export function useAvatarActions({ sessionId, projectId, onRequirementsChanged }: AvatarActionsScope) {
  const [pendingAction, setPendingAction] = useState<PendingAvatarAction | null>(null);
  const qc = useQueryClient();
  const raid = useRaidMutations({ sessionId });
  const milestones = useMilestoneMutations(projectId);

  const open = useCallback((action: AvatarRecommendedAction, contributionId: string) => {
    setPendingAction({ action, contributionId });
  }, []);

  const close = useCallback(() => setPendingAction(null), []);

  const recordDecision = useCallback(async (
    action: AvatarRecommendedAction,
    contributionId: string,
    state: 'accepted' | 'rejected',
    appliedEntityType?: string,
    appliedEntityId?: string
  ) => {
    await api.patch(`/api/sessions/${sessionId}/contributions/${contributionId}/actions/${action.id}`, {
      state, appliedEntityType, appliedEntityId,
    });
    qc.invalidateQueries({ queryKey: ['project-requirements', projectId] });
    if (state === 'accepted') {
      await onRequirementsChanged?.();
    }
  }, [sessionId, projectId, qc, onRequirementsChanged]);

  const dismiss = useCallback(async (action: AvatarRecommendedAction, contributionId: string) => {
    await recordDecision(action, contributionId, 'rejected');
  }, [recordDecision]);

  // Every write below carries source: 'avatar-action' plus a note recording
  // provenance, so the requirements set stays auditable back to the avatar
  // reply and facilitator decision that produced it.
  const provenanceNote = (action: AvatarRecommendedAction, contributionId: string) =>
    [`Created from an avatar-recommended action (contribution ${contributionId})`];

  const applyCreatePersona = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    persona: { name: string; role: string; goals: string[]; painPoints: string[] }
  ) => {
    const res = await api.post(`/api/sessions/${sessionId}/personas`, {
      ...persona, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    });
    await recordDecision(action, contributionId, 'accepted', 'persona', res.data?.persona?.id);
  }, [sessionId, recordDecision]);

  const applyCreateUserStory = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, story: Record<string, unknown>
  ) => {
    const res = await api.post(`/api/sessions/${sessionId}/user-stories`, {
      ...story, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    });
    const created = res.data?.story || res.data;
    await recordDecision(action, contributionId, 'accepted', 'user_story', created?.storyId || created?.id);
  }, [sessionId, recordDecision]);

  const applyCreateNfr = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, nfr: Record<string, unknown>
  ) => {
    const res = await api.post(`/api/sessions/${sessionId}/nfrs`, {
      ...nfr, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    });
    await recordDecision(action, contributionId, 'accepted', 'nfr', res.data?.nfr?.id);
  }, [sessionId, recordDecision]);

  const applyCreateAdr = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, adr: Record<string, unknown>
  ) => {
    const created = await createADR(sessionId!, {
      ...adr, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    } as any);
    await recordDecision(action, contributionId, 'accepted', 'adr', created.id);
  }, [sessionId, recordDecision]);

  const applyCreateFeature = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, feature: Record<string, unknown>
  ) => {
    const created = await createFeature(sessionId!, {
      ...feature, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    } as any);
    await recordDecision(action, contributionId, 'accepted', 'feature', created.id);
  }, [sessionId, recordDecision]);

  const applyCreateRaidItem = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, input: RaidItemInput
  ) => {
    const created = await raid.create.mutateAsync({
      ...input, source: 'avatar-action', notes: provenanceNote(action, contributionId),
    });
    await recordDecision(action, contributionId, 'accepted', 'raid_item', created.raidId);
  }, [raid.create, recordDecision]);

  const applyResolveRaidItem = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    raidId: string, body: { status: 'resolved' | 'deferred' | 'wont_resolve'; resolution?: string }
  ) => {
    const resolved = await raid.resolve.mutateAsync({ raidId, ...body });
    await recordDecision(action, contributionId, 'accepted', 'raid_item', resolved.raidId);
  }, [raid.resolve, recordDecision]);

  const applyStoryMutation = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    storyId: string, updates: { story?: string; acceptanceCriteria?: string[]; priority?: 'high' | 'medium' | 'low' }
  ) => {
    await updateStoryContent(sessionId!, storyId, updates);
    await recordDecision(action, contributionId, 'accepted', 'user_story', storyId);
  }, [sessionId, recordDecision]);

  // Appends a single avatar-authored note to a story (alternative-approach suggestion or
  // legacy-assumption challenge). The PATCH route replaces notes[] wholesale rather than
  // appending, so callers must pass the full merged array — never just the new note — or
  // the story's pre-existing notes are silently destroyed.
  const applyStoryNote = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    storyId: string, mergedNotes: string[]
  ) => {
    await updateStoryContent(sessionId!, storyId, { notes: mergedNotes });
    await recordDecision(action, contributionId, 'accepted', 'user_story', storyId);
  }, [sessionId, recordDecision]);

  // Tags every story in the group with a shared groupId/groupLabel an avatar proposed.
  // Avatar review is always scoped to a single persona (or one story), so every
  // storyId here is already unambiguous within that scope — no currentPersona needed.
  const applyGroupStories = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    storyIds: string[], groupLabel: string
  ) => {
    const groupId = crypto.randomUUID();
    await Promise.all(storyIds.map(id =>
      updateStoryContent(sessionId!, id, { groupId, groupLabel })
    ));
    await recordDecision(action, contributionId, 'accepted', 'user_story', storyIds.join(','));
  }, [sessionId, recordDecision]);

  const applyLinkMilestone = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction,
    milestoneId: string, target: { storyIds?: string[]; featureIds?: string[] }
  ) => {
    await milestones.assign.mutateAsync({ milestoneId, sessionId, ...target });
    await recordDecision(action, contributionId, 'accepted', 'milestone', milestoneId);
  }, [milestones.assign, sessionId, recordDecision]);

  const applyLinkRaid = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, raidId: string, link: RaidLink
  ) => {
    const updated = await raid.link.mutateAsync({ raidId, link });
    await recordDecision(action, contributionId, 'accepted', 'raid_item', updated.raidId);
  }, [raid.link, recordDecision]);

  // Fires the task immediately on confirm — action state flips to 'accepted'
  // the moment the run starts (irreversible, a task has already begun), not
  // when it later completes or fails. Task outcome is tracked on the task
  // record itself via TriggerTaskProgress/useTaskPolling, not by flipping
  // this action back to proposed/rejected.
  const applyTriggerTask = useCallback(async (
    contributionId: string, action: AvatarRecommendedAction, templateId: string, instruction: string
  ) => {
    const task = getAvatarTriggerableTask(templateId);
    if (!task) throw new Error(`"${templateId}" is not an avatar-triggerable task`);
    if (!sessionId) throw new Error('No sessionId in scope');

    const { workflowId } = await sessionTasksService.createTask(sessionId, {
      taskTemplateId: templateId,
      taskName: action.label,
      taskCategory: task.taskCategory,
      contextSnapshot: {
        customContext: { [task.instructionField]: instruction },
      },
    });
    await recordDecision(action, contributionId, 'accepted', 'session_task', workflowId);
  }, [sessionId, recordDecision]);

  return {
    pendingAction, open, close, dismiss,
    applyCreatePersona, applyCreateUserStory, applyCreateNfr, applyCreateAdr, applyCreateFeature,
    applyCreateRaidItem, applyResolveRaidItem, applyStoryMutation, applyLinkMilestone, applyLinkRaid,
    applyTriggerTask, applyStoryNote, applyGroupStories,
  };
}
